/**
 * Generador de códigos de barras CODE 128.
 *
 * Es la simbología que corresponde para lo que imprime el depósito: admite
 * letras, números y guiones, que es la forma de nuestros códigos
 * (`CAP-004821-03`). Las de comercio —EAN, UPC— codifican productos de góndola
 * y no vienen al caso acá.
 *
 * Se usan los juegos **B y C**, cambiando de uno al otro solo. El B cubre todo
 * el ASCII imprimible; el C comprime cada par de dígitos en un símbolo, o sea
 * la mitad de módulos. Eso pesa más de lo que parece: los códigos de material
 * de ZF son diez dígitos, y en juego B ocupan 145 módulos contra 90 en juego C.
 * En un recuadro de 70 mm son barras de 0,48 mm o de 0,78 mm, y en esa
 * diferencia está que el lector enganche o no.
 *
 * `code128()` devuelve anchos en módulos, sin unidad. `trazar()` los convierte
 * en milímetros, y ahí está la otra mitad del asunto: **un módulo tiene que
 * medir un número entero de puntos de la impresora**. Ver el comentario de
 * `trazar()`.
 */

/**
 * Los 107 símbolos de la norma. Cada uno son seis anchos que se leen barra,
 * espacio, barra, espacio, barra, espacio; el de parada lleva siete.
 */
const PATRONES = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
] as const;

const INICIO_B = 104;
const INICIO_C = 105;
/** Los símbolos que cambian de juego en el medio del código. */
const A_JUEGO_B = 100;
const A_JUEGO_C = 99;
const PARADA = 106;

/** El juego B arranca en el espacio: el valor es el ASCII menos 32. */
const CODIFICABLE = /^[\x20-\x7e]*$/;

export const sePuedeCodificar = (texto: string) => CODIFICABLE.test(texto);

export type CodigoDeBarras = {
  /** Anchos en módulos, empezando por barra y alternando con espacio. */
  modulos: number[];
  /** Cuántos módulos de ancho tiene el código entero. */
  total: number;
  texto: string;
};

const esDigito = (ch: string | undefined) => ch !== undefined && ch >= '0' && ch <= '9';

/** Cuántos dígitos seguidos hay a partir de esta posición. */
function digitosDesde(texto: string, i: number): number {
  let n = 0;
  while (esDigito(texto[i + n])) n++;
  return n;
}

/**
 * Traduce el texto a los valores de la norma, eligiendo el juego que dé el
 * código más corto.
 *
 * Las dos reglas son las de la norma:
 *
 * - Se arranca en juego C si el código es todo dígitos en cantidad par, o si
 *   empieza con cuatro dígitos o más. Con menos no conviene: el símbolo de
 *   arranque es uno solo, pero volver a B cuesta otro.
 * - Desde el juego B se pasa a C cuando vienen cuatro dígitos seguidos o más.
 *   Cuatro dígitos son cuatro símbolos en B contra tres en C —el cambio más
 *   dos pares—, así que ahí ya empata y de ahí en adelante se gana uno por
 *   cada par.
 *
 * Vuelve a B en cuanto aparece algo que C no puede: una letra, o el último
 * dígito de una tira impar.
 */
function valoresDe(texto: string): number[] {
  const alArranque = digitosDesde(texto, 0);
  const arrancaEnC = alArranque >= 4 || (alArranque === texto.length && alArranque >= 2 && alArranque % 2 === 0);

  const valores = [arrancaEnC ? INICIO_C : INICIO_B];
  let enC = arrancaEnC;
  let i = 0;

  while (i < texto.length) {
    const seguidos = digitosDesde(texto, i);

    if (enC) {
      // En C sólo entran pares. Un dígito solo, o cualquier otro carácter,
      // obliga a volver a B.
      if (seguidos >= 2) {
        valores.push(Number(texto.slice(i, i + 2)));
        i += 2;
      } else {
        valores.push(A_JUEGO_B);
        enC = false;
      }
      continue;
    }

    if (seguidos >= 4) {
      valores.push(A_JUEGO_C);
      enC = true;
      continue;
    }

    valores.push(texto.charCodeAt(i) - 32);
    i++;
  }

  // La verificación de la norma: el arranque pesa una vez y cada símbolo que
  // sigue pesa por su posición, los cambios de juego incluidos.
  let suma = valores[0]!;
  for (let k = 1; k < valores.length; k++) suma += valores[k]! * k;
  valores.push(suma % 103, PARADA);

  return valores;
}

/**
 * Codifica el texto. Devuelve `null` si trae algún carácter que la simbología
 * no puede representar: mejor no imprimir nada que imprimir un código que el
 * lector va a leer distinto.
 */
export function code128(texto: string): CodigoDeBarras | null {
  if (!texto || !sePuedeCodificar(texto)) return null;

  const modulos: number[] = [];
  for (const v of valoresDe(texto)) {
    for (const ancho of PATRONES[v]!) modulos.push(Number(ancho));
  }

  return { modulos, total: modulos.reduce((a, b) => a + b, 0), texto };
}

/** La impresora del depósito: 203 dpi, que son casi ocho puntos por milímetro. */
export const PUNTOS_POR_MM = 203 / 25.4;

/**
 * La zona muda que la norma pide a cada lado del código: diez módulos.
 *
 * Sin ella el lector no sabe dónde empieza el código, y lo que hay al lado
 * —la línea del recuadro de la etiqueta, por ejemplo— lo lee como si fuera una
 * barra más. En la etiqueta de pallet el recuadro pasaba a 2,5 mm de la primera
 * barra, o sea cinco módulos, y el código no se leía **nunca**: no era una
 * cuestión de suerte ni de pulso.
 */
export const ZONA_MUDA_MODULOS = 10;

/** Menos de dos puntos de impresora, la barra más fina no existe. */
export const PUNTOS_MINIMOS = 2;

/**
 * Con cuántos puntos por módulo el código se lee sin depender de la suerte.
 *
 * Dos puntos es el mínimo aritmético, pero medido contra el decodificador no
 * aguanta ni un punto de derrame térmico: la primera etiqueta que salga con la
 * impresora un poco caliente no se lee. Desde tres aguanta uno, y desde cinco,
 * dos. Debajo de tres el diseñador avisa, porque el arreglo es ensanchar el
 * recuadro y eso lo tiene que hacer una persona.
 */
export const PUNTOS_COMODOS = 3;

/** El ancho de la barra más fina que un lector puede llegar a enganchar. */
export const ANCHO_MINIMO_MM = PUNTOS_MINIMOS / PUNTOS_POR_MM;

export type Trazado = {
  /** Las barras negras, en milímetros, ya ubicadas dentro del elemento. */
  barras: { x: number; ancho: number }[];
  /** El ancho del módulo, redondeado a un número entero de puntos. */
  modulo: number;
  /** Cuántos puntos de la impresora mide ese módulo. */
  puntos: number;
  /** Lo que ocupa el código, sin contar las zonas mudas. */
  ancho: number;
  /** La zona muda que quedó a cada lado, en módulos. */
  muda: number;
  /**
   * La barra más fina quedó por debajo de lo cómodo. No quiere decir que no se
   * vaya a leer: quiere decir que hay que ensanchar el recuadro o acortar el
   * contenido para que se lea siempre y no cuando toca.
   */
  apretado: boolean;
};

/**
 * Ubica el código dentro de un elemento de `anchoTotalMm`, en milímetros.
 *
 * Hay dos cosas que se resuelven acá y que no se ven mirando la etiqueta en
 * pantalla.
 *
 * **El módulo mide un número entero de puntos de la impresora.** Estirar el
 * código para que llene el recuadro daba módulos de, por ejemplo, 3,86 puntos.
 * La impresora no sabe hacer 3,86 puntos: cada borde cae en el punto entero más
 * cercano, así que la misma barra de un módulo sale de 4 puntos o de 3 según
 * dónde le tocó caer. Un error de un punto sobre cuatro es del 25 %, y lo que
 * el lector mide son justamente las proporciones entre barras y espacios. Con
 * el módulo redondeado para abajo a puntos enteros, todos los bordes caen en la
 * grilla y las proporciones son exactas.
 *
 * **La zona muda va adentro del elemento.** Es la parte que no se puede dejar a
 * criterio de quien diseña, porque un código sin zona muda no se lee y eso no
 * se nota hasta tener el rollo puesto. El código queda centrado y los diez
 * módulos de cada lado salen del ancho que sobra.
 *
 * Redondear para abajo hace que el código no llene el recuadro hasta el borde.
 * Es a propósito: perder un milímetro de dibujo vale menos que un código que no
 * se lee.
 */
export function trazar(codigo: CodigoDeBarras, anchoTotalMm: number, puntosPorMm = PUNTOS_POR_MM): Trazado {
  const conMudas = codigo.total + ZONA_MUDA_MODULOS * 2;
  const puntos = Math.max(PUNTOS_MINIMOS, Math.floor((anchoTotalMm * puntosPorMm) / conMudas));
  const modulo = puntos / puntosPorMm;
  const ancho = codigo.total * modulo;

  // El arranque también cae en un punto entero: si no, todos los bordes quedan
  // corridos medio punto y vuelve el problema que se acaba de resolver.
  const desde = Math.round(((anchoTotalMm - ancho) / 2) * puntosPorMm) / puntosPorMm;

  const barras: { x: number; ancho: number }[] = [];
  let m = 0;
  for (const [i, w] of codigo.modulos.entries()) {
    if (i % 2 === 0) barras.push({ x: desde + m * modulo, ancho: w * modulo });
    m += w;
  }

  const muda = desde / modulo;
  return { barras, modulo, puntos, ancho, muda, apretado: puntos < PUNTOS_COMODOS };
}

/**
 * Qué ancho hay que darle a un elemento para que el código entre con módulos
 * de `puntos` puntos y sus zonas mudas. Es lo que el diseñador le sugiere a
 * quien está dibujando cuando el recuadro quedó chico.
 */
export const anchoNecesarioMm = (codigo: CodigoDeBarras, puntos = PUNTOS_COMODOS, puntosPorMm = PUNTOS_POR_MM) =>
  ((codigo.total + ZONA_MUDA_MODULOS * 2) * puntos) / puntosPorMm;
